import { useEffect, useRef, useState } from 'react';
import supabase from '../supabase/supabase';
import { loadContent, saveContent } from '../services/content';
import { useSiteContent } from '../context/SiteContent';
import { amenities as amenityOptions } from '../data/site';
import SafeIcon from '../common/SafeIcon';
import AdminImageField from '../components/AdminImageField';
import RoomDirectory from '../components/admin/RoomDirectory';
import OverviewPanel from '../components/admin/OverviewPanel';
import BookingsPanel from '../components/admin/BookingsPanel';
import SchedulePanel from '../components/admin/SchedulePanel';
import EmailSettingsPanel from '../components/admin/EmailSettingsPanel';
import './Admin.css';
import './AdminGlass.css';
import './admin-rooms.css';

function getAmenities(room) {
  const source = room.amenities ?? room.features ?? [];
  const values = Array.isArray(source) ? source : source.split(/\r?\n|·/);
  return values.map((item) => String(item).trim()).filter(Boolean);
}

function RoomListEditor({ value, onChange, placeholder }) {
  const [items, setItems] = useState(() => getAmenities({ amenities: value }));
  const [focusIndex, setFocusIndex] = useState(null);
  const inputs = useRef([]);

  useEffect(() => {
    if (focusIndex !== null && inputs.current[focusIndex]) {
      inputs.current[focusIndex].focus();
      setFocusIndex(null);
    }
  }, [focusIndex, items]);

  const updateItems = (nextItems) => {
    setItems(nextItems);
    const cleaned = nextItems.map((item) => item.trim()).filter(Boolean);
    onChange(Array.isArray(value) ? cleaned : cleaned.join('\n'));
  };
  const addItem = () => {
    setItems([...items, '']);
    setFocusIndex(items.length);
  };

  return (
    <div style={{ display: 'grid', gap: 9 }}>
      {items.map((item, index) => (
        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span aria-hidden="true" style={{ color: '#a17a24', fontWeight: 700 }}>{String(index + 1).padStart(2, '0')}</span>
          <input
            ref={(node) => { inputs.current[index] = node; }}
            aria-label={`List item ${index + 1}`}
            value={item}
            placeholder={placeholder}
            onChange={(event) => updateItems(items.map((current, position) => position === index ? event.target.value : current))}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                addItem();
              }
            }}
            style={{ flex: 1, minWidth: 0 }}
          />
          <button
            className="admin-remove"
            type="button"
            aria-label={`Remove item ${index + 1}`}
            onClick={() => updateItems(items.filter((_, position) => position !== index))}
          >
            ×
          </button>
        </div>
      ))}
      <button
        className="button button-outline"
        type="button"
        onClick={addItem}
        style={{ justifySelf: 'start' }}
      >
        + Add item
      </button>
    </div>
  );
}

function RoomEditor({ room, onSaved }) {
  const [draft, setDraft] = useState({
    ...room,
    amenities: getAmenities(room),
    keyFeatures: room.keyFeatures || '',
    images: [...room.images]
  });
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const setField = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  const setImage = (index, url) => setDraft((current) => ({
    ...current,
    images: current.images.map((image, position) => position === index ? url : image)
  }));

  function toggleAmenity(label) {
    setDraft((current) => {
      const currentAmenities = getAmenities({ amenities: current.amenities });
      const nextAmenities = currentAmenities.includes(label)
        ? currentAmenities.filter((item) => item !== label)
        : [...currentAmenities, label];
      return { ...current, amenities: nextAmenities };
    });
  }

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      await saveContent(`room-${room.number}`, {
        title: draft.title,
        description: draft.description,
        price: draft.price,
        bookingComPrice: draft.bookingComPrice || '',
        agodaPrice: draft.agodaPrice || '',
        capacity: draft.capacity,
        keyFeatures: draft.keyFeatures,
        amenities: getAmenities({ amenities: draft.amenities }),
        images: draft.images.filter(Boolean)
      });
      await onSaved();
      setMessage('Room saved.');
    } catch (error) {
      setMessage(`Could not save room: ${error.message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="admin-card" onSubmit={submit}>
      <div className="admin-card-heading"><span>ROOM {String(room.number).padStart(2, '0')}</span><h2>{draft.title}</h2></div>
      <div className="admin-fields">
        {['title', 'description', 'price', 'bookingComPrice', 'agodaPrice', 'capacity', 'keyFeatures', 'amenities'].map((key) => {
          const Wrapper = ['keyFeatures', 'amenities'].includes(key) ? 'div' : 'label';
          return (
          <Wrapper className="form-field" key={key}>
            <span>{{
              title: 'Title',
              description: 'Description',
              price: 'Price',
              bookingComPrice: 'Booking.com price',
              agodaPrice: 'Agoda.com price',
              capacity: 'Capacity',
              keyFeatures: 'Key Features:',
              amenities: 'Amenities'
            }[key]}</span>
            {['keyFeatures', 'amenities'].includes(key) ? (
              <>
                {key === 'amenities' && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))', gap: 9, marginBottom: 12 }}>
                    {amenityOptions.map((option) => {
                      const active = getAmenities({ amenities: draft.amenities }).includes(option.label);
                      return (
                        <button
                          key={option.label}
                          type="button"
                          aria-pressed={active}
                          onClick={() => toggleAmenity(option.label)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 9,
                            padding: '11px 12px',
                            border: `1px solid ${active ? '#d9ae4b' : '#ffffffb3'}`,
                            borderRadius: 14,
                            background: active ? 'rgba(217, 174, 75, .18)' : 'rgba(255, 255, 255, .5)',
                            color: '#302d27',
                            cursor: 'pointer',
                            textAlign: 'left'
                          }}
                        >
                          <SafeIcon name={option.icon.replace(/^Fi/, '')} />
                          <span style={{ flex: 1 }}>{option.label}</span>
                          <span aria-hidden="true">{active ? '✓' : '+'}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
                <RoomListEditor
                  value={draft[key]}
                  placeholder={key === 'amenities' ? 'Add one amenity per line' : ''}
                  onChange={(value) => setField(key, value)}
                />
              </>
            ) : ['description', 'keyFeatures', 'amenities'].includes(key)
              ? <textarea
                  rows={key === 'amenities' ? 5 : 3}
                  placeholder={key === 'amenities' ? 'Add one amenity per line' : ''}
                  value={key === 'amenities' ? draft.amenities.join('\n') : draft[key]}
                  onChange={(event) => setField(
                    key,
                    key === 'amenities'
                      ? event.target.value.split(/\r?\n/).map((item) => item.trim()).filter(Boolean)
                      : event.target.value
                  )}
                />
              : <input value={draft[key] || ''} onChange={(event) => setField(key, event.target.value)} />}
          </Wrapper>
          );
        })}
      </div>
      <h3>Room photos</h3>
      <p>The first photo is the room card image. Additional photos appear in the room carousel.</p>
      {draft.images.map((image, index) => (
        <div className="admin-image-row" key={index}>
          <AdminImageField label={`Photo ${index + 1}`} value={image} onChange={(url) => setImage(index, url)} />
          {index > 0 && <button className="admin-remove" type="button" onClick={() => setField('images', draft.images.filter((_, position) => position !== index))}>Remove photo</button>}
        </div>
      ))}
      <button className="button button-outline" type="button" onClick={() => setField('images', [...draft.images, ''])}>Add another photo</button>
      <div className="admin-actions"><button className="button button-gold" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save room'}</button><span role="status">{message}</span></div>
    </form>
  );
}

function ImagesEditor({ site, onSaved }) {
  const [draft, setDraft] = useState({
    hero: site.images.hero,
    about: site.images.about,
    gallery: [...site.images.gallery]
  });
  const [message, setMessage] = useState('');
  const updateGallery = (index, url) => setDraft((current) => ({
    ...current,
    gallery: current.gallery.map((image, position) => index === position ? url : image)
  }));

  async function submit(event) {
    event.preventDefault();
    setMessage('');
    try {
      await saveContent('images', draft);
      await onSaved();
      setMessage('Website images saved.');
    } catch (error) {
      setMessage(`Could not save images: ${error.message}`);
    }
  }

  return (
    <form className="admin-card" onSubmit={submit}>
      <div className="admin-card-heading"><span>VISUALS</span><h2>Website images</h2></div>
      <p>Change the hero, About, and five gallery photos. Gallery edits update both the home preview and Gallery page.</p>
      <div className="admin-image-list">
        <AdminImageField label="Hero background" value={draft.hero} onChange={(url) => setDraft((current) => ({ ...current, hero: url }))} />
        <AdminImageField label="About / lounge" value={draft.about} onChange={(url) => setDraft((current) => ({ ...current, about: url }))} />
        {draft.gallery.map((image, index) => (
          <AdminImageField key={index} label={`Gallery photo ${index + 1}`} value={image} onChange={(url) => updateGallery(index, url)} />
        ))}
      </div>
      <div className="admin-actions"><button className="button button-gold" type="submit">Save website images</button><span role="status">{message}</span></div>
    </form>
  );
}

export default function Admin() {
  const { site, reload, error: contentError } = useSiteContent();
  const [session, setSession] = useState(null);
  const [authorized, setAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [notice, setNotice] = useState('');
  const [tab, setTab] = useState('overview');
  const [selectedRoomNumber, setSelectedRoomNumber] = useState(null);
  const [addingRoom, setAddingRoom] = useState(false);
  const [roomNotice, setRoomNotice] = useState('');
  const selectedRoom = site.rooms.find((room) => room.number === selectedRoomNumber);

  useEffect(() => {
    let current = true;
    async function check(userSession) {
      if (!current) return;
      setSession(userSession);
      if (!userSession?.user) {
        setAuthorized(false);
        setLoading(false);
        return;
      }
      const { data, error } = await supabase.from('guest_house_admins_1700000000000')
        .select('user_id').eq('user_id', userSession.user.id).maybeSingle();
      if (!current) return;
      setAuthorized(Boolean(data) && !error);
      setNotice(error ? error.message : '');
      setLoading(false);
    }
    supabase.auth.getSession().then(({ data }) => check(data.session));
    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setTimeout(() => check(nextSession), 0);
    });
    return () => { current = false; listener.subscription.unsubscribe(); };
  }, []);

  async function signIn(event) {
    event.preventDefault();
    setNotice('');
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) setNotice(error.message);
  }

  async function addRoom() {
    const nextNumber = Math.max(0, ...site.rooms.map((room) => room.number)) + 1;
    setAddingRoom(true);
    setRoomNotice('');
    try {
      await saveContent(`room-${nextNumber}`, {
        title: `New room ${nextNumber}`,
        description: '',
        price: '',
        capacity: '',
        keyFeatures: '',
        amenities: [],
        images: []
      });
      await reload();
      setSelectedRoomNumber(nextNumber);
    } catch (error) {
      setRoomNotice(`Could not add room: ${error.message}`);
    } finally {
      setAddingRoom(false);
    }
  }

  if (loading) return <main className="admin-shell"><div className="page-width admin-loading">Checking admin access…</div></main>;

  if (!session || !authorized) return (
    <main className="admin-shell">
      <div className="admin-login">
        <span className="eyebrow">Private workspace</span>
        <h1>Guest house admin</h1>
        <p>Sign in with a pre-approved Supabase administrator account to manage the website.</p>
        {!session ? (
          <form className="site-form" onSubmit={signIn}>
            <label className="form-field"><span>Email</span><input type="email" autoComplete="username" value={email} onChange={(event) => setEmail(event.target.value)} required /></label>
            <label className="form-field"><span>Password</span><input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required /></label>
            <button className="button button-gold" type="submit">Sign in</button>
          </form>
        ) : <button className="button button-outline" type="button" onClick={() => supabase.auth.signOut()}>Sign out</button>}
        {session && <p role="alert">This account is not on the administrator allowlist.</p>}
        {notice && <p role="alert">{notice}</p>}
      </div>
    </main>
  );

  return (
    <main className="admin-shell">
      <div className="page-width admin-layout">
        <aside className="admin-sidebar">
          <span className="admin-sidebar-title">ADMIN PANEL</span>
          <span className="admin-sidebar-label">MANAGE</span>
          <div className="admin-tabs" role="tablist" aria-label="Admin sections">
            <button type="button" role="tab" aria-selected={tab === 'overview'} onClick={() => setTab('overview')}>Overview</button>
            <button type="button" role="tab" aria-selected={tab === 'bookings'} onClick={() => setTab('bookings')}>Enquiries</button>
            <button type="button" role="tab" aria-selected={tab === 'schedule'} onClick={() => setTab('schedule')}>Room schedule</button>
            <button type="button" role="tab" aria-selected={tab === 'rooms'} onClick={() => { setTab('rooms'); setSelectedRoomNumber(null); setRoomNotice(''); }}>Rooms</button>
            <button type="button" role="tab" aria-selected={tab === 'images'} onClick={() => setTab('images')}>Website images</button>
            <button type="button" role="tab" aria-selected={tab === 'settings'} onClick={() => setTab('settings')}>Settings</button>
          </div>
          <a className="button button-outline" href="/" target="_blank" rel="noopener noreferrer" style={{ width: '100%', marginTop: 'auto' }}>Visit site</a>
        </aside>
        <section className="admin-content">
          <div className="admin-topline"><div><span className="eyebrow">K and son’s Guest House</span><p>Keep every room and image just the way you want it.</p></div><button className="button button-outline" type="button" onClick={() => supabase.auth.signOut()}>Sign out</button></div>
          {contentError && <p className="admin-warning" role="alert">{contentError}</p>}
          {tab === 'overview' && <OverviewPanel roomCount={site.rooms.length} rooms={site.rooms} />}
          {tab === 'bookings' && <BookingsPanel />}
          {tab === 'schedule' && <SchedulePanel rooms={site.rooms} />}
          {tab === 'rooms' && !selectedRoom && (
            <RoomDirectory
              rooms={site.rooms}
              onSelect={(number) => setSelectedRoomNumber(number)}
              onAdd={addRoom}
              adding={addingRoom}
              notice={roomNotice}
            />
          )}
          {tab === 'rooms' && selectedRoom && (
            <div className="admin-room-editor-view">
              <button className="button button-outline admin-room-back" type="button" onClick={() => setSelectedRoomNumber(null)}>
                ← All rooms
              </button>
              <RoomEditor key={selectedRoom.number} room={selectedRoom} onSaved={reload} />
            </div>
          )}
          {tab === 'images' && <ImagesEditor site={site} onSaved={reload} />}
          {tab === 'settings' && <EmailSettingsPanel />}
        </section>
      </div>
    </main>
  );
}